import videojs from 'video.js';
import 'videojs-sprite-thumbnails';

const _enabled = new WeakMap();
const _width = new WeakMap();
const _height = new WeakMap();
const _columns = new WeakMap();
const _rows = new WeakMap();
const _interval = new WeakMap();
const _aspectRatio = new WeakMap();

const REFERENCE_WIDTH = 1920;
const MIN_DOWNLINK_SPEED_MBPS = 0; // default is 1.5, we set it to 0 to disable the downlink check (MVP #2429)

/**
 * The Thumbnails module.
 * @member {ThumbnailsPlugin} seekThumbnails
 * @memberof MediasitePlayer#
 */

/**
 * Configures seek thumbnails to show an image preview on seek or hover over the seek bar.
 * @hideconstructor
 * @extends {external:"videojs.Plugin"}
 **/
class SeekThumbnailsPlugin extends videojs.getPlugin('plugin') {
    constructor(player, options) {
        super(player, options);
    }

    /**
     * Initializes the plugin with the given data.
     * @param {Object} options
     * @example
     * player.thumbnails.init({});
     */
    init(options) {
        if (!options || !Object.keys(options)) {
            this.enabled = false;
            return;
        }

        this.height = options.Height;
        this.width = options.Width;
        if (options.Height && options.Width) {
            this.aspectRatio = options.Width / options.Height;
        }

        this.player.spriteThumbnails ? this.player.spriteThumbnails({
            interval: options.Interval,
            url: options.URL,
            idxTag: function (index) {
                return `000${index + 1}`.slice(-4);
            },
            width: this.width,
            height: this.height,
            columns: options.Columns,
            rows: options.Rows,
            downlink: MIN_DOWNLINK_SPEED_MBPS,
            responsive: REFERENCE_WIDTH
        }) : console.error("Failed to initialize sprite thumbnails plugin.");
    }

    /**
     * Whether or not to enable the thumbnails UI.
     * @type {boolean}
     * @example
     * player.thumbnails.enabled = true;
     */
    get enabled() {
        return _enabled.get(this) ?? false;
    }

    set enabled(enabled) {
        if (!enabled) {
            this.player.removeClass('vjs-sprite-thumbnails');
        }
        _enabled.set(this, enabled);
    }

    /**
     * The desired display width of the thumbnail.
     * @type {number}
     * @example
     * player.thumbnails.width = 500;
     */
    get width() {
        return _width.get(this); //* ratio;
    }

    set width(width) {
        _width.set(this, width);
    }

    /**
     * The desired display height of the thumbnail
     * @type {number}
     * @example
     * player.thumbnails.height = 100;
     */
    get height() {
        return _height.get(this);
    }

    set height(height) {
        _height.set(this, height);
    }

    /**
     * Aspect ratio of the thumbnail image.
     * @type {boolean}
     * @example
     * player.thumbnails.aspectRatio = 1.8;
     */
    get aspectRatio() {
        let ratio = _aspectRatio.get(this);
        if (!ratio && this.width && this.height) {
            ratio = this.width / this.height;
        }
        return ratio ?? 1.78;
    }

    set aspectRatio(ratio) {
        _aspectRatio.set(this, ratio);
    }

    /**
     * The number of images stacked horizontally in the thumbnail source image
     * @type {number}
     * @example
     * player.thumbnails.columns = 5;
     */
    get columns() {
        return _columns.get(this);
    }

    set columns(columns) {
        _columns.set(this, columns);
    }

    /**
     * The number of images stacked vertically in the thumbnail source image
     * @type {number}
     * @example
     * player.thumbnails.rows = 5;
     */
    get rows() {
        return _rows.get(this);
    }

    set rows(rows) {
        _rows.set(this, rows);
    }

    /**
     * The thumbnail refresh rate in seconds
     * @type {number}
     * @example
     * player.thumbnails.interval = 10;
     */
    get interval() {
        return _interval.get(this);
    }

    set interval(interval) {
        _interval.set(this, interval);
    }
}

export default SeekThumbnailsPlugin;

export function fixTimeTooltipUpdate(player) {

    // attempt to find the timeline tooltip
    const timeTooltip = player.getChild('ControlBar')?.getChild('ProgressControl')?.getChild('SeekBar')?.getChild('MouseTimeDisplay')?.getChild('TimeTooltip');
    if (timeTooltip != null) {

        // override the update function with the fix that has been added to later versions of videojs: https://github.com/videojs/video.js/issues/8273
        timeTooltip.update = (function () {
            return function (seekBarRect, seekBarPoint, content) {
                var tooltipRect = videojs.dom.findPosition(this.el_);
                var playerRect = videojs.dom.getBoundingClientRect(this.player_.el());
                var seekBarPointPx = seekBarRect.width * seekBarPoint; // do nothing if either rect isn't available
                // for example, if the player isn't in the DOM for testing

                if (!playerRect || !tooltipRect) {
                    return;
                } // This is the space left of the `seekBarPoint` available within the bounds
                // of the player. We calculate any gap between the left edge of the player
                // and the left edge of the `SeekBar` and add the number of pixels in the
                // `SeekBar` before hitting the `seekBarPoint`


                var spaceLeftOfPoint = seekBarRect.left - playerRect.left + seekBarPointPx; // This is the space right of the `seekBarPoint` available within the bounds
                // of the player. We calculate the number of pixels from the `seekBarPoint`
                // to the right edge of the `SeekBar` and add to that any gap between the
                // right edge of the `SeekBar` and the player.

                var spaceRightOfPoint = seekBarRect.width - seekBarPointPx + (playerRect.right - seekBarRect.right); // This is the number of pixels by which the tooltip will need to be pulled
                // further to the right to center it over the `seekBarPoint`.

                // spaceRightOfPoint is always NaN for mouse time display
                // because the seekbarRect does not have a right property. This causes
                // the mouse tool tip to be truncated when it's close to the right edge of the player.
                // In such cases, we ignore the `playerRect.right - seekBarRect.right` value when calculating.
                // For the sake of consistency, we ignore seekBarRect.left - playerRect.left for the left edge.
                if (!spaceRightOfPoint) {
                    spaceRightOfPoint = seekBarRect.width - seekBarPointPx;
                    spaceLeftOfPoint = seekBarPointPx;
                }

                var pullTooltipBy = tooltipRect.width / 2; // Adjust the `pullTooltipBy` distance to the left or right depending on
                // the results of the space calculations above.

                if (spaceLeftOfPoint < pullTooltipBy) {
                    pullTooltipBy += pullTooltipBy - spaceLeftOfPoint;
                } else if (spaceRightOfPoint < pullTooltipBy) {
                    pullTooltipBy = spaceRightOfPoint;
                } // Due to the imprecision of decimal/ratio based calculations and varying
                // rounding behaviors, there are cases where the spacing adjustment is off
                // by a pixel or two. This adds insurance to these calculations.


                if (pullTooltipBy < 0) {
                    pullTooltipBy = 0;
                } else if (pullTooltipBy > tooltipRect.width) {
                    pullTooltipBy = tooltipRect.width;
                } // prevent small width fluctuations within 0.4px from
                // changing the value below.
                // This really helps for live to prevent the play
                // progress time tooltip from jittering


                pullTooltipBy = Math.round(pullTooltipBy);
                this.el_.style.right = "-" + pullTooltipBy + "px";
                this.write(content);
            }
        }());
    }
}
